import { useRef, useState } from 'react'
import { FileUp, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { SistemaImportPreview } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { PlrBadge } from './PlrBadge'

type ImportSistemaDialogProps = {
  isOpen: boolean
  onClose: () => void
  preview: SistemaImportPreview | null
  fileName: string | null
  isPreviewLoading: boolean
  isImporting: boolean
  previewError: string | null
  onPickFile: (file: File) => void
  onConfirmImport: () => void
}

export function ImportSistemaDialog({
  isOpen,
  onClose,
  preview,
  fileName,
  isPreviewLoading,
  isImporting,
  previewError,
  onPickFile,
  onConfirmImport,
}: ImportSistemaDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [step, setStep] = useState<'pick' | 'confirm'>('pick')

  const handleClose = () => {
    if (isImporting) {
      return
    }
    setStep('pick')
    onClose()
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose()
    }
  }

  const showConfirmStep = step === 'confirm' && fileName != null

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className='max-h-[90vh] sm:max-w-lg'
        showCloseButton={!isImporting && !isPreviewLoading}
      >
        <DialogHeader>
          <DialogTitle>
            {t('safetyFunctionsPage.importDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <input
          ref={fileInputRef}
          type='file'
          accept='.ssm,application/xml,text/xml'
          className='hidden'
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file != null) {
              setStep('confirm')
              onPickFile(file)
            }
            event.target.value = ''
          }}
        />

        {!showConfirmStep ? (
          <div className='space-y-4'>
            <p className='text-sm text-muted-foreground'>
              {t('safetyFunctionsPage.importDialog.description')}
            </p>
            <Button
              type='button'
              variant='outline'
              className='w-full'
              disabled={isPreviewLoading}
              onClick={() => fileInputRef.current?.click()}
            >
              {isPreviewLoading ? (
                <Loader2 className='mr-2 size-4 animate-spin' />
              ) : (
                <FileUp className='mr-2 size-4' />
              )}
              {t('safetyFunctionsPage.importDialog.chooseFile')}
            </Button>
            {previewError != null ? (
              <Alert variant='destructive'>
                <AlertTitle>{t('safetyFunctionsPage.errorTitle')}</AlertTitle>
                <AlertDescription>{previewError}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        ) : (
          <div className='space-y-4'>
            <p className='text-sm text-muted-foreground'>
              {t('safetyFunctionsPage.importDialog.selectedFile', {
                fileName,
              })}
            </p>

            {isPreviewLoading ? (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Loader2 className='size-4 animate-spin' />
                {t('safetyFunctionsPage.importDialog.previewLoading')}
              </div>
            ) : null}

            {!isPreviewLoading && preview != null ? (
              <>
                {preview.projectName != null ? (
                  <p className='text-sm'>
                    <span className='font-medium'>
                      {t('safetyFunctionsPage.importDialog.projectName')}
                    </span>{' '}
                    {preview.projectName}
                  </p>
                ) : null}

                <Alert variant='destructive'>
                  <AlertTitle>
                    {t('safetyFunctionsPage.importDialog.replaceWarningTitle')}
                  </AlertTitle>
                  <AlertDescription className='space-y-2'>
                    <p>
                      {t('safetyFunctionsPage.importDialog.replaceWarningBody')}
                    </p>
                    <ul className='list-disc space-y-1 ps-4'>
                      <li>
                        {t(
                          'safetyFunctionsPage.importDialog.replaceWarningDeleteSafetyFunctions',
                          { count: preview.existing.safetyFunctionCount }
                        )}
                      </li>
                      <li>
                        {t(
                          'safetyFunctionsPage.importDialog.replaceWarningDeleteArchitecture'
                        )}
                      </li>
                      {preview.existing.linkedRiskReductionMeasureCount > 0 ? (
                        <li>
                          {t(
                            'safetyFunctionsPage.importDialog.replaceWarningUnlinkMeasures',
                            {
                              count:
                                preview.existing
                                  .linkedRiskReductionMeasureCount,
                            }
                          )}
                        </li>
                      ) : null}
                    </ul>
                  </AlertDescription>
                </Alert>

                <div className='space-y-2'>
                  <p className='text-sm font-medium'>
                    {t('safetyFunctionsPage.importDialog.incomingSummary', {
                      count: preview.incoming.safetyFunctionCount,
                    })}
                  </p>
                  <ScrollArea className='max-h-48 rounded-md border'>
                    <ul className='divide-y p-2'>
                      {preview.incoming.safetyFunctions.map(
                        (safetyFunction, index) => (
                          <li
                            key={`${index}-${safetyFunction.name}`}
                            className='flex items-start justify-between gap-2 text-sm'
                          >
                            <span className='min-w-0'>
                              {safetyFunction.name}
                            </span>
                            <PlrBadge plr={safetyFunction.plcal} />
                          </li>
                        )
                      )}
                    </ul>
                  </ScrollArea>
                </div>

                {previewError != null ? (
                  <Alert variant='destructive'>
                    <AlertTitle>
                      {t('safetyFunctionsPage.errorTitle')}
                    </AlertTitle>
                    <AlertDescription>{previewError}</AlertDescription>
                  </Alert>
                ) : null}
              </>
            ) : null}
          </div>
        )}

        <DialogFooter>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={isImporting || isPreviewLoading}
          >
            {t('common:actions.cancel')}
          </Button>
          {showConfirmStep ? (
            <Button
              variant='destructive'
              onClick={onConfirmImport}
              disabled={
                isImporting ||
                isPreviewLoading ||
                preview == null ||
                previewError != null
              }
            >
              {isImporting ? (
                <Loader2 className='mr-2 size-4 animate-spin' />
              ) : null}
              {t('safetyFunctionsPage.importDialog.confirmReplace')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
